import { get } from 'lodash';

/* eslint-disable @typescript-eslint/no-explicit-any */
export class ConditionProcessorUtil {
  static processConditionsWithPermission = (conditions: { key: string; value: any }[][], userPermissions: { key: string; value: boolean }[]) => {
    if (!conditions || (conditions && conditions.length === 0)) {
      return true;
    } else {
      return conditions.reduce((andConditionArrayResult, orConditionArray) => {
        const orConditionResult = orConditionArray.reduce((conditionResult, condition) => {
          const currentPermission = userPermissions?.find((permission) => permission.key === condition.key);
          if (currentPermission) {
            conditionResult = conditionResult || currentPermission.value === condition.value;
          }
          return conditionResult;
        }, false);

        return andConditionArrayResult && orConditionResult;
      }, true);
    }
  };

  static processConditionsWithElement = (
    conditions: { key: string; value: any }[][],
    element: any,
    forEnable: boolean,
    userPermissions?: { key: string; value: boolean }[]
  ) => {
    if (!conditions || conditions.length === 0) {
      return forEnable;
    }

    const hasPermissions = userPermissions && userPermissions.length > 0;

    if (hasPermissions) {
      // Combined mode — outer: OR between groups · inner: AND between source types (OR within same source type)
      // [[A, B]]: A from element AND B from permissions must both pass (AND between sources)
      // [[A], [B]]: element group A OR permission group B passing is sufficient (OR between groups)
      return conditions.reduce((outerOrResult, orConditionArray) => {
        let elemGroupResult: boolean | undefined; // undefined = no element conditions in this group
        let permGroupResult: boolean | undefined; // undefined = no permission conditions in this group

        for (const condition of orConditionArray) {
          const currentPermission = userPermissions.find((p) => p.key === condition.key);
          if (currentPermission !== undefined) {
            // Permission-sourced condition — OR within permission source
            const condResult = currentPermission.value === condition.value;
            permGroupResult = permGroupResult === undefined ? condResult : permGroupResult || condResult;
          } else {
            // Element-sourced condition — OR within element source
            let condResult = false;
            if (element) {
              const val = ConditionProcessorUtil.getValue(element, condition.key);
              if (Array.isArray(val)) {
                condResult = (val).includes(ConditionProcessorUtil.getValue(condition, 'value'));
              } else {
                condResult = val === ConditionProcessorUtil.getValue(condition, 'value');
              }
            }
            elemGroupResult = elemGroupResult === undefined ? condResult : elemGroupResult || condResult;
          }
        }

        // AND between sources (no conditions for a source type → neutral true)
        const elemResult = elemGroupResult === undefined ? true : elemGroupResult;
        const permResult = permGroupResult === undefined ? true : permGroupResult;
        return outerOrResult || (elemResult && permResult);
      }, false);
    }

    // Element-only mode (original behaviour) — outer: AND between groups · inner: OR within group
    if (!element) {
      return forEnable;
    }
    return conditions.reduce((andConditionArrayResult, orConditionArray) => {
      const orConditionResult = orConditionArray.reduce((conditionResult, condition) => {
        let currentPermissionResult: boolean;
        if (Array.isArray(ConditionProcessorUtil.getValue(element, condition.key))) {
          const elementValues = ConditionProcessorUtil.getValue(element, condition.key) as any[];
          currentPermissionResult = elementValues.includes(ConditionProcessorUtil.getValue(condition, 'value'));
        } else {
          currentPermissionResult = ConditionProcessorUtil.getValue(element, condition.key) === ConditionProcessorUtil.getValue(condition, 'value');
        }
        conditionResult = conditionResult || currentPermissionResult;
        return conditionResult;
      }, false);
      return andConditionArrayResult && orConditionResult;
    }, true);
  };

  private static getValue(element: any, key: string): string | any[] {
    const value = get(element, key, '');
    if (value) {
      if (Array.isArray(value)) {
        return value;
      } else {
        return value.toString().toLowerCase();
      }
    }
    return '';
  }
}
